/**
 * Support module DTOs (§11.4, §15.2 `POST /v1/support/cases`): participants get a case ID, owner, next update
 * time and appeal path; cases link to the win/claim timeline without exposing unrestricted ID documents.
 */
import { z } from 'zod';
import { E164Phone, IsoDateTime, paginated, SocialHandle, Ulid } from './common.js';
import { ClaimStatus, SocialPlatform, SupportCaseStatus } from './enums.js';
import { SupportCaseRef } from './ids.js';
export const SupportCategory = z.enum([
    'PAYMENT',
    'ENTRY',
    'CLAIM',
    'VERIFICATION_APPEAL',
    'FULFILLMENT',
    'LOST_CLAIM_LINK',
    'ACCESSIBILITY',
    'CONSENT',
    'OTHER',
]);
export const SupportPriority = z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']);
export const CreateSupportCaseRequest = z.object({
    category: SupportCategory,
    message: z.string().trim().min(10).max(4000),
    /** Any of these links the case to the right timeline; none is required. */
    entryId: Ulid.optional(),
    paymentId: Ulid.optional(),
    winId: z.string().optional(),
    lotterySlug: z.string().max(64).optional(),
    contact: z
        .discriminatedUnion('kind', [
        z.object({ kind: z.literal('WHATSAPP'), value: E164Phone }),
        z.object({ kind: z.literal('SOCIAL_HANDLE'), platform: SocialPlatform, value: SocialHandle }),
        z.object({ kind: z.literal('EMAIL'), value: z.email() }),
    ])
        .optional(),
    /** Lost claim link: identity is re-established through the approved recovery, never by win ID alone. */
    recoveryRequested: z.boolean().default(false),
});
export const SupportCaseDto = z.object({
    caseRef: SupportCaseRef,
    status: SupportCaseStatus,
    category: SupportCategory,
    priority: SupportPriority,
    owner: z.string().nullable(),
    nextUpdateAt: IsoDateTime.nullable(),
    appealPath: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    /** Participant-visible messages only. */
    messages: z.array(z.object({ from: z.enum(['PARTICIPANT', 'SUPPORT']), text: z.string(), at: IsoDateTime })),
});
export const SupportCaseAdminDto = SupportCaseDto.extend({
    caseId: Ulid,
    tenantId: Ulid,
    participantId: Ulid.nullable(),
    links: z.object({
        entryId: Ulid.nullable(),
        paymentId: Ulid.nullable(),
        winId: z.string().nullable(),
        claimId: Ulid.nullable(),
        lotteryId: Ulid.nullable(),
    }),
    assignedTo: Ulid.nullable(),
    internalNotes: z.array(z.object({ staffId: Ulid, text: z.string(), at: IsoDateTime })),
    escalated: z.boolean(),
    slaBreached: z.boolean(),
});
export const UpdateSupportCaseRequest = z.object({
    status: SupportCaseStatus.optional(),
    assignTo: Ulid.nullable().optional(),
    priority: SupportPriority.optional(),
    nextUpdateAt: IsoDateTime.optional(),
    participantMessage: z.string().max(4000).optional(),
    internalNote: z.string().max(4000).optional(),
    requestInformation: z.string().max(1000).optional(),
    externalFollowUp: z.object({ platform: SocialPlatform, summary: z.string().max(1000) }).optional(),
});
export const SupportCaseListQuery = z.object({
    status: SupportCaseStatus.optional(),
    category: SupportCategory.optional(),
    priority: SupportPriority.optional(),
    assignedTo: Ulid.optional(),
    winId: z.string().optional(),
});
// ---------------------------------------------------------------------------------------------------------------
// Support module extensions (support agent, D-1709…).
// ---------------------------------------------------------------------------------------------------------------
/** Priority → stored integer (1 urgent … 4 low) and the next-update commitment in hours (§11.4). */
export const SUPPORT_PRIORITY_RANK = {
    URGENT: 1,
    HIGH: 2,
    NORMAL: 3,
    LOW: 4,
};
export const SUPPORT_NEXT_UPDATE_HOURS = {
    URGENT: 4,
    HIGH: 24,
    NORMAL: 48,
    LOW: 72,
};
export const supportPriorityFromRank = (rank) => rank <= 1 ? 'URGENT' : rank === 2 ? 'HIGH' : rank === 3 ? 'NORMAL' : 'LOW';
/** Default appeal window after a rejected claim when the claim carries none (§11.2). */
export const SUPPORT_APPEAL_WINDOW_DAYS = 7;
export const SupportCasePageDto = paginated(SupportCaseAdminDto);
/** Evidence timeline entry: claim/win states and evidence *kinds* only — never documents or references (§11.4). */
export const SupportTimelineEntryDto = z.object({
    at: IsoDateTime,
    kind: z.enum(['CASE', 'CLAIM', 'WIN', 'EVIDENCE', 'VERIFICATION', 'FULFILLMENT', 'FOLLOW_UP']),
    title: z.string(),
    detail: z.string().nullable(),
    claimStatus: ClaimStatus.nullable().optional(),
});
/** `GET /v1/admin/support/cases/:id` superset with the evidence timeline and the external follow-ups. */
export const SupportCaseAdminDetailDto = SupportCaseAdminDto.extend({
    timeline: z.array(SupportTimelineEntryDto),
    externalFollowUps: z.array(z.object({ platform: SocialPlatform, summary: z.string(), staffId: Ulid.nullable(), at: IsoDateTime })),
    informationRequests: z.array(z.object({ text: z.string(), staffId: Ulid.nullable(), at: IsoDateTime })),
    appealDeadlineAt: IsoDateTime.nullable(),
    recoveryRequested: z.boolean(),
});
//# sourceMappingURL=support.js.map